import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AsyncPipe, DOCUMENT, NgIf } from '@angular/common';

import Swal from 'sweetalert2';
import { showErrorSwal } from '@asamco/utils';

import { TourOverlayComponent } from '../tour/tour-overlay.component';

import { IntegrationTesterFacade } from './state/integration-tester.facade';
import { Vm } from './state/integration-tester.models';

import { ItTopbarComponent } from './components/topbar/it-topbar.component';
import { ItContextBarComponent } from './components/context-bar/it-context-bar.component';
import { ItSidebarComponent } from './components/sidebar/it-sidebar.component';
import { ItEditorComponent } from './components/editor/it-editor.component';
import { ItResponsePaneComponent } from './components/response/it-response-pane.component';
import { ItSplitterComponent } from './components/splitter/it-splitter.component';

@Component({
  selector: 'app-shell-integration-tester',
  standalone: true,
  templateUrl: './integration-tester-shell.component.html',
  styleUrls: ['./integration-tester-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    AsyncPipe,

    ItTopbarComponent,
    ItContextBarComponent,
    ItSidebarComponent,
    ItEditorComponent,
    ItSplitterComponent,
    ItResponsePaneComponent,

    TourOverlayComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class IntegrationTesterShellComponent implements OnInit, OnDestroy {
  readonly vm$ = this.facade.vm$;
  readonly theme$ = this.facade.theme$;
  readonly renderedBodyHtml$ = this.facade.renderedBodyHtml$;
  readonly pathTreeItems$ = this.facade.pathTreeItems$;

  constructor(
    public readonly facade: IntegrationTesterFacade,
    @Inject(DOCUMENT) private readonly doc: Document,
  ) { }

  ngOnInit(): void {
    this.doc.body.classList.add('it-no-main-padding');
    this.facade.init();
  }

  ngOnDestroy(): void {
    this.facade.dispose();
    this.doc.body.classList.remove('it-no-main-padding');
  }

  // ---- Swal prompts live here (recommended) ----

  async addCollection(): Promise<void> {
    const res = await Swal.fire({
      title: 'New collection',
      input: 'text',
      inputPlaceholder: 'Collection name',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });

    if (!res.isConfirmed) return;
    const name = (res.value ?? '').toString().trim();
    if (!name) return;
    this.facade.createCollection(name);
  }

  async renameCollection(vm: Vm): Promise<void> {
    if (!vm.activeCollection) return;

    const res = await Swal.fire({
      title: 'Rename collection',
      input: 'text',
      inputValue: vm.activeCollection.name,
      showCancelButton: true,
      confirmButtonText: 'Save',
    });

    if (!res.isConfirmed) return;
    const name = (res.value ?? '').toString().trim();
    if (!name) return;
    this.facade.renameActiveCollection(name);
  }

  async deleteCollection(): Promise<void> {
    const res = await Swal.fire({
      title: 'Delete collection?',
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!res.isConfirmed) return;
    this.facade.deleteActiveCollection();
  }

  async addRequest(): Promise<void> {
    const res = await Swal.fire({
      title: 'New request',
      input: 'text',
      inputPlaceholder: 'Request name',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });

    if (!res.isConfirmed) return;
    this.facade.createRequest((res.value ?? '').toString());
  }

  async deleteRequest(): Promise<void> {
    const res = await Swal.fire({
      title: 'Delete request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!res.isConfirmed) return;
    this.facade.deleteActiveRequest();
  }

  testLogin(): void {
    this.facade.testLogin().subscribe({
      next: () => Swal.fire({ icon: 'success', title: 'Login is valid', timer: 1400 }),
      error: err => {
        const raw = (err as any)?.error ?? err; // HttpErrorResponse -> payload
        Swal.fire({
          icon: 'error',
          title: 'Login test failed',
          text: JSON.stringify(raw, null, 2),
          confirmButtonText: 'OK',
        });
      },
    });
  }

  getIntegrationCrumb(vm: Vm): string {
    const id = vm.selectedIntegrationId;
    if (!id) return 'Integration';
    return (vm.integrations ?? []).find(x => x.id === id)?.name ?? 'Integration';
  }

  async send(): Promise<void> {
    await this.facade.send();
  }
}
